import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Users } from 'lucide-react';
import type { GenealogyNode } from '@mlm/shared';
import { fmtPeso, network, wallet } from '../lib/api';
import { qk } from '../lib/queries';
import { CoachButton, Eyebrow, ErrorNote, Loading, Screen } from '../components/primitives';
import { ShopNav } from '../components/shop-nav';

type View = 'unilevel' | 'binary';
type Crumb = { slotId: string; label: string };

/**
 * The genealogy, as a drill-down rather than a drawn tree. A pan-and-zoom tree
 * is unusable on a phone, so each screen shows ONE slot and its direct children
 * and tapping a child re-roots there. The breadcrumb is the way back up.
 *
 * Which binary fields are visible is a tenant setting enforced server-side, so
 * every optional field here is rendered only when it actually arrived.
 */
export function NetworkPage() {
  const [view, setView] = useState<View>('unilevel');
  const [trail, setTrail] = useState<Crumb[]>([]);

  const slots = useQuery({ queryKey: qk.mySlots, queryFn: wallet.slots, staleTime: 5 * 60_000 });
  const rootSlotId = slots.data?.[0]?.id ?? null;
  const focus = trail.length > 0 ? trail[trail.length - 1]!.slotId : rootSlotId;

  const tree = useQuery({
    queryKey: qk.genealogy(focus ?? '', view),
    queryFn: () => network.genealogy({ slotId: focus!, view, depth: 2 }),
    enabled: Boolean(focus),
    staleTime: 60_000,
  });

  const nodes = tree.data?.nodes ?? [];
  // depth 0 is the slot in focus; depth 1 are its direct children.
  const self = nodes.find((n) => n.depth === 0) ?? null;
  const children = nodes.filter((n) => n.depth === 1);

  const descend = (n: GenealogyNode) =>
    setTrail((t) => [...t, { slotId: n.slotId, label: n.userFullName ?? n.slotCode }]);

  const noSlot = !slots.isPending && (slots.data?.length ?? 0) === 0;

  return (
    <Screen eyebrow="Shop" title="Your network" wide>
      <ShopNav />

      {slots.isPending ? (
        <Loading label="Loading" />
      ) : noSlot ? (
        <div className="px-5 py-14 text-center">
          <Eyebrow>No network yet</Eyebrow>
          <p className="mx-auto mt-2 max-w-sm text-[0.93rem] leading-relaxed text-ink/70">
            Your network starts once you activate a membership slot.
          </p>
        </div>
      ) : (
        <>
          <div className="flex gap-2 border-b border-border px-5 py-4">
            {(['unilevel', 'binary'] as const).map((v) => (
              <CoachButton
                key={v}
                kind={view === v ? 'gold' : 'outline'}
                onClick={() => {
                  setView(v);
                  setTrail([]);
                }}
              >
                {v === 'unilevel' ? 'Sponsor tree' : 'Binary'}
              </CoachButton>
            ))}
          </div>

          {trail.length > 0 && (
            <nav aria-label="Path" className="border-b border-border px-5 py-3">
              <ol className="flex flex-wrap items-center gap-1 text-[0.85rem]">
                <li>
                  <button
                    type="button"
                    onClick={() => setTrail([])}
                    className="text-sage underline underline-offset-4 hover:text-ink"
                  >
                    You
                  </button>
                </li>
                {trail.map((c, i) => (
                  <li key={c.slotId} className="flex items-center gap-1">
                    <ChevronRight className="h-3 w-3 text-sage-deep" aria-hidden />
                    {i === trail.length - 1 ? (
                      <span className="text-ink">{c.label}</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setTrail((t) => t.slice(0, i + 1))}
                        className="text-sage underline underline-offset-4 hover:text-ink"
                      >
                        {c.label}
                      </button>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          )}

          {tree.isPending ? (
            <Loading label="Loading the tree" />
          ) : tree.error ? (
            <ErrorNote error={tree.error} retry={() => void tree.refetch()} />
          ) : (
            <>
              {self && <FocusCard node={self} view={view} currency={tree.data!.currency} />}

              <p className="border-b border-border px-5 py-3 font-mono text-[0.62rem] uppercase tracking-[0.14em] text-sage-deep">
                {children.length === 0
                  ? 'No one below yet'
                  : children.length === 1
                    ? '1 directly below'
                    : `${children.length} directly below`}
              </p>

              <ul>
                {children.map((n) => (
                  <li key={n.slotId} className="border-b border-border">
                    <button
                      type="button"
                      onClick={() => descend(n)}
                      disabled={n.childCount === 0}
                      className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-surface disabled:cursor-default disabled:hover:bg-transparent"
                    >
                      {view === 'binary' && n.position && (
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center border border-border font-mono text-[0.62rem] text-sage-deep">
                          {n.position}
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[0.93rem] text-ink">
                          {n.userFullName ?? n.slotCode}
                        </span>
                        <span className="mt-0.5 block text-[0.85rem] text-sage-deep">
                          {n.slotCode}
                          {n.membershipName ? ` · ${n.membershipName}` : ''}
                          {n.descendantCount > 0 ? ` · ${n.descendantCount} below` : ''}
                        </span>
                      </span>
                      {n.childCount > 0 && (
                        <ChevronRight className="h-4 w-4 shrink-0 text-sage-deep" aria-hidden />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </Screen>
  );
}

function FocusCard({
  node,
  view,
  currency,
}: {
  node: GenealogyNode;
  view: View;
  currency: string;
}) {
  return (
    <div className="border-b border-border px-5 py-6">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-border bg-surface text-sage-deep">
          <Users className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="font-display text-[1.25rem] font-semibold leading-tight text-ink">
            {node.userFullName ?? node.slotCode}
          </p>
          <p className="mt-1 text-[0.85rem] text-sage-deep">
            {node.slotCode}
            {node.membershipName ? ` · ${node.membershipName}` : ''}
            {node.rankCode ? ` · ${node.rankCode}` : ''}
          </p>
        </div>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-4">
        <Stat label="Directly below" value={String(node.childCount)} />
        <Stat label="Whole team" value={String(node.descendantCount)} />
        {/* Binary-only, and only when the tenant lets a member see them. */}
        {view === 'binary' && node.leftPv != null && (
          <Stat label="Left points" value={node.leftPv} />
        )}
        {view === 'binary' && node.rightPv != null && (
          <Stat label="Right points" value={node.rightPv} />
        )}
        {node.packageAmount != null && (
          <Stat label="Package" value={`${currency} ${fmtPeso(node.packageAmount)}`} />
        )}
        {node.sponsorName != null && <Stat label="Sponsor" value={node.sponsorName} />}
      </dl>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="eyebrow">{label}</dt>
      <dd className="mt-1 font-display text-[1.1rem] font-semibold text-ink">{value}</dd>
    </div>
  );
}
